import { useEffect, useMemo, useState } from "react";
import AuiButton from "../Buttons/Buttons";
import DropDown from "../DropDown/DropDown";
import {
  getCountry,
  getState,
} from "../../services/training-session.service";
import SearchableSingleSelectDropdown from "../DynamicDropdowns/SearchableSingleSelectDropdown";
import { useDebouncedSearch } from "../../utils/constants/app-constants";
import { mergeSelectedWithList } from "../Hooks/classDetailMultiDropdownHook";

export interface DropdownOption {
  id: number;
  name: string;
  code?: string;
  regionId?: number;
  regionCode?: string;
  regionName?: string;
}

interface FilterProps {
  setOrgType: (orgType: DropdownOption | null) => void;
  setCountry: (country: DropdownOption | null) => void;
  setState: (state: DropdownOption | null) => void;
  setRegion: (region: DropdownOption | null) => void;
  setCurrentPage: (page: number) => void;
}

const OrganizationFilterSection: React.FC<FilterProps> = ({
  setOrgType,
  setCountry,
  setState,
  setRegion,
  setCurrentPage,
}) => {
  const organizationTypes: DropdownOption[] = [
    { id: 1, name: "Certifying Organization" },
    { id: 2, name: "Sponsoring Organization" },
  ];

  const [countries, setCountries] = useState<DropdownOption[]>([]);
  const [states, setStates] = useState<DropdownOption[]>([]);
  const [regions, setRegions] = useState<DropdownOption[]>([]);

  const [selectedOrgType, setSelectedOrgType] = useState<DropdownOption | null>(null);
  const [selectedCountry, setSelectedCountry] = useState<DropdownOption | null>(null);
  const [selectedState, setSelectedState] = useState<DropdownOption | null>(null);
  const [selectedRegion, setSelectedRegion] = useState<DropdownOption | null>(null);
  const [isDataLoading, setIsDataLoading] = useState(true);
  
  const mergedCountryList = useMemo(
    () => mergeSelectedWithList(countries, selectedCountry, "code"),
    [countries, selectedCountry]
  );


  const mergedStateList = useMemo(
    () => mergeSelectedWithList(states, selectedState, "code"),
    [states, selectedState]
  );
  
  const fetchCountries = async (search: string) => {
        setIsDataLoading(true);
        const fetchedCountries = await getCountry(search);
        setCountries(fetchedCountries);
        setRegions(fetchedCountries);
        setIsDataLoading(false);
    };


      
    const fetchStates = async (search: string) => {
        if (!selectedCountry?.code) return;
        setIsDataLoading(true);
        const fetchedStates = await getState(selectedCountry.code ?? "", search);
        setStates(fetchedStates);
        setIsDataLoading(false);
    };

  const debouncedSearchCountries = useDebouncedSearch(fetchCountries);
  const debouncedSearchStates = useDebouncedSearch(fetchStates);

   useEffect(() => {
    fetchCountries("");
  }, []);

  useEffect(() => {
  setRegions([]);
  setSelectedRegion(null);
  setStates([]);
  setSelectedState(null);

  if (selectedCountry?.id) {
    setSelectedRegion(selectedCountry)
    debouncedSearchStates("");
  }
}, [selectedCountry]);




  
  const handleSelectionChange = (
    value: string,
    options: DropdownOption[],
    setSelected: React.Dispatch<React.SetStateAction<DropdownOption | null>>
  ) => {
    const selectedOption = options.find((option) => option.name === value);
    setSelected(selectedOption || null);
  };

  const handleGoClick = () => {
    setOrgType(selectedOrgType);
    setCountry(selectedCountry);
    setState(selectedState);
    setRegion(selectedRegion);
    setCurrentPage(1);
  };

  return (
    <div className="orgFilterSection my-4">
      <div className="row d-flex">
        <div className="col-12 col-md-6 col-lg-3">
          <DropDown
            listInBtn
            data={organizationTypes.map((org) => org.name)}
            dropdownLabel="Type"
            placeHolder="All Organizations"
            dropId="OrganizationsType"
            onChange={(value) =>
              handleSelectionChange(value, organizationTypes, setSelectedOrgType)
            }
          />
        </div>
        <div className="col-12 col-md-6 col-lg-3">
          <SearchableSingleSelectDropdown
            listInBtn
            data={mergedCountryList.map((country: any) => country.name)}
            value={selectedCountry?.name ?? ""}
            dropdownLabel="Country"
            placeHolder="All Countries"
            mainId="Country"
            isLoading={isDataLoading}
            onChange={(value) =>
              handleSelectionChange(value, countries, setSelectedCountry)
            }
            onSearch={debouncedSearchCountries}
            dropId="Country"
          />
        </div>
        <div className="col-12 col-md-6 col-lg-3">
        <SearchableSingleSelectDropdown
            listInBtn
            data={mergedCountryList.map((region:any) => region.regionName)}
            value={selectedRegion?.regionName ?? ""}
            dropdownLabel="Region"
            placeHolder="All Regions"
            mainId="Region"
            isLoading={isDataLoading && !!selectedCountry?.name}
            disabled={true}
            onChange={(value) =>
              handleSelectionChange(value, regions, setSelectedRegion)
            }
            dropId="Region"
          />
        </div>
        <div className="col-12 col-md-6 col-lg-3">
        <SearchableSingleSelectDropdown
            listInBtn
            data={mergedStateList.map((state: any) => state.name)}
            value={selectedState?.name ?? ""}
            dropdownLabel="State"
            placeHolder="All States"
            mainId="State"
            isLoading={isDataLoading && !!selectedCountry?.name}
            disabled={!selectedCountry}
            onChange={(value) =>
              handleSelectionChange(value, states, setSelectedState)
            }
            onSearch={debouncedSearchStates}
            dropId="State"
          />
        </div>
      </div>
      <div className="row d-flex justify-content-end my-4 mr-0">
        <AuiButton
          variant="primary"
          buttontitle="Go"
          buttonid="filterOrg"
          size="medium"
          buttonClick={handleGoClick}
        />
      </div>
    </div>
  );
};

export default OrganizationFilterSection;
